import { useState, useRef } from 'react';
import type { InputRef } from 'antd';
import { Form } from 'antd';

import { PIN_DIGIT_REGEX, PIN_LENGTH, PIN_PASTE_REGEX } from '../constants';

type PinArray = string[];
type PinInputRefArray = (InputRef | null)[];

interface PinInputHook {
	handleKeyDown: (index: number, e: React.KeyboardEvent) => void;
	handlePinChange: (index: number, value: string) => void;
	pinInputRefs: React.RefObject<PinInputRefArray>;
	handlePaste: (e: React.ClipboardEvent) => void;
	fillPinAutomatically: (pin: string) => void;
	pinValues: PinArray;
}

export function usePinInput(): PinInputHook {
	const [pinValues, setPinValues] = useState<PinArray>(Array(PIN_LENGTH).fill(''));
	const pinInputRefs = useRef<PinInputRefArray>(Array(PIN_LENGTH).fill(null));
	const formInstance = Form.useFormInstance();

	function updateFormPinValue(newPinValues: PinArray): void {
		formInstance.setFieldsValue({ pin: newPinValues });
	}

	function focusInput(index: number): void {
		if (index >= 0 && index < PIN_LENGTH) {
			pinInputRefs.current[index]?.focus();
		}
	}

	function handlePinChange(index: number, value: string): void {
		const upperCaseValue = value.toUpperCase();

		if (!PIN_DIGIT_REGEX.test(upperCaseValue)) return;

		const newPinValues = [...pinValues];
		newPinValues[index] = upperCaseValue;
		setPinValues(newPinValues);
		updateFormPinValue(newPinValues);

		if (upperCaseValue && index < PIN_LENGTH - 1) {
			focusInput(index + 1);
		}
	}

	function handleKeyDown(index: number, e: React.KeyboardEvent): void {
		if (e.key === 'Backspace') {
			if (!pinValues[index] && index > 0) {
				focusInput(index - 1);
			} else if (pinValues[index] && index >= 0) {
				const newPinValues = [...pinValues];
				newPinValues[index] = '';
				setPinValues(newPinValues);
				updateFormPinValue(newPinValues);
			}
		}
	}

	function handlePaste(e: React.ClipboardEvent): void {
		e.preventDefault();

		const pastedText = e.clipboardData.getData('text').replace(/\s/g, '');
		if (!PIN_PASTE_REGEX.test(pastedText)) return;

		const digits = pastedText.substring(0, PIN_LENGTH);
		const newPinValues = [...pinValues];

		for (let i = 0; i < digits.length; i++) {
			newPinValues[i] = digits[i].toUpperCase();
		}

		setPinValues(newPinValues);
		updateFormPinValue(newPinValues);

		const nextEmptyIndex = newPinValues.findIndex((val) => val === '');
		const focusIndex = nextEmptyIndex === -1 ? PIN_LENGTH - 1 : nextEmptyIndex;
		focusInput(focusIndex);
	}

	function fillPinAutomatically(pin: string): void {
		if (!pin) return;

		const pinDigits = pin.split('').slice(0, PIN_LENGTH);
		const paddedPinDigits = [...pinDigits, ...Array(PIN_LENGTH).fill('')].slice(0, PIN_LENGTH);

		setPinValues(paddedPinDigits);
		updateFormPinValue(paddedPinDigits);
	}

	return {
		pinValues,
		pinInputRefs,
		handlePinChange,
		handleKeyDown,
		handlePaste,
		fillPinAutomatically
	};
}
